import { supabase } from '../supabase';
// Adicionar imports para gamificação
import { processAction } from '../gamification/gamificationService';
import { XP_ACTIONS } from '../gamification/xpConfig';

// Função para avaliar um jogo
export async function rateGame(userId: string, gameId: string, rating: number) {
  try {
    // Validar rating
    if (rating < 0 || rating > 10) {
      throw new Error('A avaliação deve ser um número entre 0 e 10');
    }
    
    const { data: existingEntry, error: queryError } = await supabase
      .from('user_games')
      .select('id') // Selecionar apenas o id para verificar existência
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .single();
    
    if (queryError && queryError.code !== 'PGRST116') { 
      throw queryError;
    }
    
    let savedUserGame;
    let isNewRating = false; // Flag para saber se é a primeira avaliação ou uma atualização
    
    if (existingEntry) {
      // Atualizar registro existente
      const { data, error } = await supabase
        .from('user_games')
        .update({
          rating,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingEntry.id)
        .select() // Selecionar o registro atualizado
        .single();
      
      if (error) throw error;
      savedUserGame = data;
    } else {
      // Criar novo registro
      isNewRating = true;
      const newEntry = {
        user_id: userId,
        game_id: gameId,
        owned: false,
        wishlist: false,
        play_count: 0,
        rating,
        notes: null
      };
      
      const { data, error } = await supabase
        .from('user_games')
        .insert(newEntry)
        .select() // Selecionar o registro inserido
        .single();
      
      if (error) throw error;
      savedUserGame = data;
    }
    
    // Atualizar a média de avaliações do jogo
    // Considerar mover para trigger/função no banco ou executar de forma assíncrona?
    await updateGameAverageRating(gameId);

    // Chamar gamificação APÓS sucesso da avaliação
    if (savedUserGame) {
        const gamificationResult = await processAction({
            supabase: supabase, // Passando o cliente explicitamente
            userId: userId,
            actionKey: XP_ACTIONS.RATE_GAME,
            sourceId: savedUserGame.id, // ID da entrada em user_games
            details: { 
                gameId: gameId,
                rating: rating,
                isUpdate: !isNewRating // Informa se foi uma atualização ou nova avaliação
            }
        });

        if (!gamificationResult.success) {
            console.warn(`Gamification warning (rateGame ${gameId}): ${gamificationResult.error}`);
        } else {
             console.log(`Gamification: User ${userId} gained ${gamificationResult.xpGained} XP for rating game ${gameId}`);
        }
    }
    
    return savedUserGame;
  } catch (error) {
    console.error(`Erro ao avaliar jogo ${gameId} pelo usuário ${userId}:`, error);
    throw error;
  }
}

// Função para atualizar a média de avaliações de um jogo
async function updateGameAverageRating(gameId: string) {
  try {
    // Buscar todas as avaliações para o jogo
    const { data: ratings, error: ratingsError } = await supabase
      .from('user_games')
      .select('rating')
      .eq('game_id', gameId)
      .not('rating', 'is', null);
    
    if (ratingsError) throw ratingsError;
    
    if (!ratings || ratings.length === 0) {
      // Se não houver avaliações, definir como null
      const { error: updateError } = await supabase
        .from('games')
        .update({ average_rating: null })
        .eq('id', gameId);
      
      if (updateError) throw updateError;
      return;
    }
    
    // Calcular a média
    const sum = ratings.reduce((acc, curr) => acc + (curr.rating || 0), 0);
    const average = sum / ratings.length;
    const roundedAverage = Math.round(average * 100) / 100; // Arredondar para 2 casas decimais
    
    // Atualizar o jogo
    const { error: updateError } = await supabase
      .from('games')
      .update({ average_rating: roundedAverage })
      .eq('id', gameId);
    
    if (updateError) throw updateError;
    
    console.log(`Média de avaliações atualizada para o jogo ${gameId}: ${roundedAverage} (${ratings.length} avaliações)`);
  } catch (error) {
    console.error(`Erro ao atualizar média de avaliações do jogo ${gameId}:`, error);
    throw error;
  }
}

// Função para atualizar a média de todos os jogos do banco
export async function updateAllGamesAverageRating() {
  try {
    console.log('Iniciando atualização das médias de avaliação de todos os jogos...');
    
    // Buscar todos os jogos
    const { data: games, error: gamesError } = await supabase
      .from('games')
      .select('id');
    
    if (gamesError) throw gamesError;
    
    if (!games || games.length === 0) {
      console.log('Nenhum jogo encontrado para atualizar avaliações.');
      return {
        success: true,
        message: 'Nenhum jogo encontrado para atualizar',
        count: 0
      };
    }
    
    console.log(`Encontrados ${games.length} jogos para atualizar avaliações.`);
    
    // Para cada jogo, buscar suas avaliações e atualizar a média
    const batchSize = 25; // Processar em lotes para evitar sobrecarga
    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };
    
    for (let i = 0; i < games.length; i += batchSize) {
      const batch = games.slice(i, i + batchSize);
      console.log(`Processando lote ${i / batchSize + 1}/${Math.ceil(games.length / batchSize)}, ${batch.length} jogos`);
      
      // Processar lote
      const promises = batch.map(async (game) => {
        try {
          await updateGameAverageRating(game.id);
          results.success++;
          return null;
        } catch (error) {
          results.failed++;
          const errorMessage = `Erro ao atualizar jogo ${game.id}: ${error}`;
          results.errors.push(errorMessage);
          console.error(errorMessage);
          return errorMessage;
        }
      });
      
      await Promise.all(promises);
      
      // Pequena pausa entre lotes para não sobrecarregar o banco
      if (i + batchSize < games.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    console.log('Atualização das médias de avaliação concluída:', {
      total: games.length,
      success: results.success,
      failed: results.failed
    });
    
    return {
      success: true,
      message: `Atualização concluída. Sucesso: ${results.success}, Falha: ${results.failed}`,
      total: games.length,
      updated: results.success,
      failed: results.failed,
      errors: results.errors
    };
  } catch (error) {
    console.error('Erro ao atualizar médias de avaliação:', error);
    throw error;
  }
}

// <<< NOVA FUNÇÃO: rateGameComplexity >>>
export async function rateGameComplexity(userId: string, gameId: string, complexityRating: number | null) {
  try {
    // Validar rating (se não for nulo)
    if (complexityRating !== null && (complexityRating < 1 || complexityRating > 5)) {
      throw new Error('A avaliação de complexidade deve ser um número entre 1 e 5');
    }

    // 1. Verificar se já existe entrada e qual era o rating anterior
    const { data: existingEntry, error: queryError } = await supabase
      .from('user_games')
      .select('id, complexity_rating') // Buscar também o rating anterior
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .maybeSingle(); // Usar maybeSingle para tratar caso não exista

    if (queryError) {
      console.error(`[rateGameComplexity] Erro ao buscar user_games:`, queryError);
      throw queryError;
    }
    
    // Armazenar o valor anterior
    const previousComplexityRating = existingEntry?.complexity_rating ?? null;

    // 2. Determinar se XP deve ser concedido (somente se avaliação válida e anterior era nula)
    const shouldGrantXp = complexityRating !== null && previousComplexityRating === null;

    let savedUserGameId: string | undefined;
    let isNewEntry = !existingEntry;

    // 3. Atualizar ou Inserir no Banco
    if (existingEntry) {
      // Atualizar registro existente
      savedUserGameId = existingEntry.id;
      const { error: updateError } = await supabase
        .from('user_games')
        .update({
          complexity_rating: complexityRating,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingEntry.id);

      if (updateError) throw updateError;
    } else {
      // Criar novo registro se não existir
      const newEntry = {
        user_id: userId,
        game_id: gameId,
        complexity_rating: complexityRating,
        owned: false,
        wishlist: false,
        play_count: 0,
        rating: null,
        notes: null
      };

      const { data: insertedData, error: insertError } = await supabase
        .from('user_games')
        .insert(newEntry)
        .select('id') 
        .single();

      if (insertError) throw insertError;
      savedUserGameId = insertedData?.id;
    }

    // 4. Chamar gamificação SOMENTE se aplicável
    if (savedUserGameId && shouldGrantXp) {
      try {
          console.log(`[rateGameComplexity] Concedendo XP para ${XP_ACTIONS.RATE_COMPLEXITY}`);
          const gamificationResult = await processAction({
              supabase: supabase,
              userId: userId,
              actionKey: XP_ACTIONS.RATE_COMPLEXITY,
              sourceId: savedUserGameId, // ID da entrada em user_games
              details: {
                  gameId: gameId,
                  complexity_rating: complexityRating, // Passar o rating atual
                  isNewEntry: isNewEntry // Pode ser útil saber se foi insert ou update
              }
          });

          if (!gamificationResult.success) {
              console.warn(`Gamification warning (rateGameComplexity ${gameId}): ${gamificationResult.error}`);
          } else {
               console.log(`Gamification: User ${userId} gained ${gamificationResult.xpGained} XP for rating complexity of game ${gameId}`);
          }
      } catch (xpError) {
           console.error(`[rateGameComplexity] Erro ao chamar processAction para ${XP_ACTIONS.RATE_COMPLEXITY}:`, xpError);
      }
    } else if (savedUserGameId && !shouldGrantXp) {
        console.log(`[rateGameComplexity] XP não concedido (rating: ${complexityRating}, previous: ${previousComplexityRating})`);
    } else if (!savedUserGameId) {
        console.warn(`[rateGameComplexity] Não foi possível salvar a entrada user_games para ${gameId}, gamificação não acionada.`);
    }

    // Considerar atualizar a média de complexidade do jogo aqui também, se necessário
    // await updateGameAverageComplexity(gameId);

    return { success: true, userGameId: savedUserGameId };

  } catch (error) {
    console.error(`Erro ao avaliar complexidade do jogo ${gameId} pelo usuário ${userId}:`, error);
    throw error;
  }
}

export { updateGameAverageRating }; 